import { useEffect, useState } from "react";
import { ChartBar, ArrowClockwise, Warning, DownloadSimple, FilePdf, FileZip, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useHousehold } from "@/context/HouseholdContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Stat = ({ label, value, testid }) => (
  <div className="bg-card border border-border rounded-xl p-4" data-testid={testid}>
    <p className="eyebrow text-[10px] text-muted-foreground">{label}</p>
    <p className="font-serif text-3xl mt-1 tabular-nums">{value}</p>
  </div>
);
const Map = ({ obj }) => (
  <div className="flex flex-wrap gap-1.5">
    {Object.entries(obj || {}).map(([k, v]) => (
      <span key={k} className="px-2 py-0.5 rounded-full bg-muted border border-border text-xs">{k.replace(/_/g, " ")}: <b className="tabular-nums">{v}</b></span>
    ))}
    {Object.keys(obj || {}).length === 0 && <span className="text-xs text-muted-foreground">none</span>}
  </div>
);

const PURGE_CONFIRM = "WIPE_LEDGER_DATA_PRESERVE_CONFIG";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};
const fmtSize = (b) => (b > 1024 ? `${(b / 1024).toFixed(1)} KB` : `${b} B`);

function SnapshotModal({ hid, onClose }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/backfill-snapshots?household_id=${hid}`)
      .then((r) => setSnapshots(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [hid]);

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/admin/backfill-snapshot/generate?household_id=${hid}`);
      toast.success("Snapshot generated — your audit artifact is ready to download.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snapshot generation failed");
    } finally { setGenerating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="snapshot-modal">
      <div className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-border">
          <div>
            <p className="eyebrow text-[10px] text-[hsl(var(--accent))]">Audit artifact</p>
            <h2 className="font-serif text-2xl mt-1">Backfill snapshots</h2>
            <p className="text-sm text-muted-foreground mt-1.5">Generate a timestamped, immutable PDF + CSV bundle for your CPA or attorney.</p>
          </div>
          <button onClick={onClose} data-testid="snapshot-modal-close" className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
        </div>

        <div className="px-6 py-4 border-b border-border">
          <Button onClick={generate} disabled={generating} data-testid="generate-snapshot-button" className="w-full sm:w-auto">
            <DownloadSimple size={16} className="mr-2" />
            {generating ? "Generating…" : "Generate new snapshot"}
          </Button>
        </div>

        <div className="overflow-y-auto px-6 py-4 space-y-3" data-testid="snapshot-list">
          {loading ? (
            <p className="text-sm text-muted-foreground py-6 text-center animate-pulse">Loading…</p>
          ) : snapshots.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No snapshots yet. Generate your first audit artifact above.</p>
          ) : snapshots.map((s) => (
            <div key={s.id} className="border border-border rounded-xl p-4" data-testid={`snapshot-row-${s.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-medium text-sm">{fmtDate(s.generated_at)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    by {s.generated_by} · {s.state_at_generation?.apu_count ?? 0} numbers · {s.state_at_generation?.document_count ?? 0} docs · {s.state_at_generation?.reconciliation_count ?? 0} recs
                  </p>
                </div>
                <div className="flex gap-2">
                  {s.files?.map((f) => (
                    <a key={f.kind} href={f.url} target="_blank" rel="noreferrer" download={f.filename}
                       data-testid={`snapshot-download-${f.kind}-${s.id}`}
                       className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors">
                      {f.kind === "pdf" ? <FilePdf size={15} /> : <FileZip size={15} />}
                      {f.kind === "pdf" ? "PDF" : "CSV"}
                      <span className="text-muted-foreground">{fmtSize(f.size_bytes)}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function BackfillSummary() {
  const { hid } = useHousehold();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmText, setConfirmText] = useState("");
  const [purging, setPurging] = useState(false);
  const [showSnapshot, setShowSnapshot] = useState(false);

  const load = () => {
    if (!hid) return;
    setLoading(true);
    api.get(`/admin/backfill-summary?household_id=${hid}`).then((r) => setS(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [hid]);

  const purge = async () => {
    setPurging(true);
    try {
      const r = await api.post(`/admin/purge-ledger`, { confirm: confirmText, household_id: hid, operator: "david" });
      toast.success(`Ledger purged. Audit ${r.data.purge_audit_id.slice(0, 8)}`);
      setConfirmText(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally { setPurging(false); }
  };

  if (loading || !s) return <div className="py-24 text-center text-sm uppercase tracking-[0.3em] text-muted-foreground animate-pulse">Loading summary…</div>;

  return (
    <div className="space-y-8 animate-fade-up" data-testid="backfill-summary">
      <div className="flex items-start justify-between gap-4 pb-6 border-b border-border">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center"><ChartBar size={22} /></div>
          <div><p className="eyebrow">Admin</p><h1 className="page-title text-[36px] mt-1">Backfill summary</h1>
            <p className="text-sm text-muted-foreground mt-2">What the ledger now knows about your portfolio.</p></div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowSnapshot(true)} data-testid="open-snapshot-modal"><DownloadSimple size={14} className="mr-1.5" /> Download snapshot</Button>
          <Button variant="outline" size="sm" onClick={load}><ArrowClockwise size={14} className="mr-1.5" /> Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat testid="bf-apus" label="Numbers" value={s.apus.total_count} />
        <Stat testid="bf-documents" label="Documents" value={s.documents.total_count} />
        <Stat testid="bf-reconciliations" label="Reconciliations" value={s.reconciliations.total_count} />
        <Stat testid="bf-bank-apus" label="Bank facts" value={s.plaid.bank_apus_count} />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <section className="bg-card border border-border rounded-xl p-5 space-y-3">
          <h2 className="font-semibold">Numbers by status</h2><Map obj={s.apus.by_trust_state} />
          <h2 className="font-semibold pt-2">By owner</h2><Map obj={s.apus.by_owned_by} />
          <h2 className="font-semibold pt-2">By fact type</h2><Map obj={s.apus.by_fact_type} />
        </section>
        <section className="bg-card border border-border rounded-xl p-5 space-y-3">
          <h2 className="font-semibold">Mineral bundles</h2>
          <p className="text-sm text-muted-foreground">{s.mineral_specific.bundles_converted} converted · cross-foot {s.mineral_specific.cross_foot_pass_count} pass / {s.mineral_specific.cross_foot_fail_count} fail</p>
          <h2 className="font-semibold pt-2">Owner resolved</h2><Map obj={s.mineral_specific.by_owner_resolved} />
          <h2 className="font-semibold pt-2">Exceptions</h2><Map obj={s.exceptions} />
        </section>
      </div>

      <section className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border"><h2 className="font-semibold">Per property</h2></div>
        {s.per_property_summary.length === 0 ? <p className="px-5 py-6 text-sm text-muted-foreground">No property data yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="px-5 py-2">Property</th><th className="px-5 py-2">Numbers</th><th className="px-5 py-2">Verified</th><th className="px-5 py-2">Periods</th></tr></thead>
            <tbody>{s.per_property_summary.map((p) => (
              <tr key={p.property_id} className="border-b border-border/60"><td className="px-5 py-2 font-medium">{p.property_label}</td><td className="px-5 py-2 tabular-nums">{p.apu_count}</td><td className="px-5 py-2 tabular-nums">{Math.round(p.reconciled_pct * 100)}%</td><td className="px-5 py-2 tabular-nums">{p.periods_with_data}</td></tr>
            ))}</tbody>
          </table>
        )}
      </section>

      {/* Danger zone — purge */}
      <section className="border border-[hsl(var(--destructive)/0.4)] rounded-xl p-5 bg-[hsl(var(--destructive)/0.04)]" data-testid="danger-zone">
        <h2 className="font-semibold flex items-center gap-2 text-[hsl(var(--destructive))]"><Warning size={18} /> Clean-slate reset</h2>
        <p className="text-sm text-muted-foreground mt-2">Permanently deletes all ledger data for this household (numbers, documents, reconciliations, mineral data, bank links). Users, owner-code mappings, and fact types are preserved. This cannot be undone.</p>
        <p className="text-xs text-muted-foreground mt-3 mb-1">Type <code className="font-mono bg-muted px-1 rounded">{PURGE_CONFIRM}</code> to enable:</p>
        <div className="flex gap-2 max-w-xl">
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={PURGE_CONFIRM} data-testid="purge-confirm-input" className="font-mono text-xs" />
          <Button variant="destructive" disabled={confirmText !== PURGE_CONFIRM || purging} onClick={purge} data-testid="purge-button">{purging ? "Purging…" : "Purge ledger"}</Button>
        </div>
      </section>
      {showSnapshot && <SnapshotModal hid={hid} onClose={() => setShowSnapshot(false)} />}
    </div>
  );
}
